import React, { FormEvent, useState } from "react";
import axios from "axios";
import type { FuelPlanFeedbackLayout } from "types/fuelPlanFeedbackLayout";
import {
  FuelPlanFeedbackRequest,
  FuelPlanFeedbackSnapshot,
  FuelPlanSurveyResult,
  FuelPlanSurveyStatus,
} from "types/fuelPlanFeedback";

type FuelPlanSurveyProps = {
  loadNumber: string;
  snapshot: FuelPlanFeedbackSnapshot;
  renderLayout?: FuelPlanFeedbackLayout;
  checkStatus: () => Promise<FuelPlanSurveyStatus>;
  submitSurvey: (
    request: FuelPlanFeedbackRequest
  ) => Promise<FuelPlanSurveyResult>;
};

const FuelPlanSurvey = ({
  loadNumber,
  snapshot,
  checkStatus,
  submitSurvey,
  renderLayout,
}: FuelPlanSurveyProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [wasPreviouslySubmitted, setWasPreviouslySubmitted] = useState(false);
  const [emailSent, setEmailSent] = useState(true);
  const [problemDescription, setProblemDescription] = useState("");
  const [expectedBehavior, setExpectedBehavior] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const openSurvey = async () => {
    setIsChecking(true);
    setErrorMessage("");
    try {
      const status = await checkStatus();
      if (!status.canSubmit) {
        setErrorMessage("Feedback is not available for this load.");
        return;
      }
      setAlreadySubmitted(status.alreadySubmitted);
      setWasPreviouslySubmitted(status.alreadySubmitted);
      setEmailSent(status.emailSent);
      if (!status.alreadySubmitted) setIsOpen(true);
    } catch {
      setErrorMessage(
        "We couldn't check feedback for this load. Please try again."
      );
    } finally {
      setIsChecking(false);
    }
  };

  const retryEmail = async () => {
    setIsChecking(true);
    setErrorMessage("");
    try {
      const status = await checkStatus();
      setEmailSent(status.emailSent);
      if (!status.emailSent)
        setErrorMessage(
          "Email delivery is still pending. Please try again later."
        );
    } catch {
      setErrorMessage(
        "We couldn't retry the email notification. Please try again."
      );
    } finally {
      setIsChecking(false);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!problemDescription.trim() || isSubmitting) return;
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const result = await submitSurvey({
        loadNumber,
        problemDescription: problemDescription.trim(),
        expectedBehavior: expectedBehavior.trim() || undefined,
        snapshot,
      });
      if (!result.submitted) throw new Error("Feedback was not saved.");
      setAlreadySubmitted(true);
      setWasPreviouslySubmitted(result.alreadySubmitted);
      setEmailSent(result.emailSent);
      setIsOpen(false);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        setAlreadySubmitted(true);
        setWasPreviouslySubmitted(true);
        setIsOpen(false);
      } else {
        setErrorMessage("We couldn't submit your feedback. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const action = !isOpen && !alreadySubmitted && (
    <button
      className="fuel-plan-survey-open"
      disabled={isChecking}
      onClick={openSurvey}
      type="button"
    >
      {isChecking ? "Checking..." : "Report a problem"}
    </button>
  );
  const details = (
    <div className="fuel-plan-survey-details">
      {isOpen && (
        <form className="fuel-plan-survey-form" onSubmit={handleSubmit}>
          <label className="fuel-plan-survey-comment">
            What problem did you encounter with the fuel plan?
            <textarea
              required
              maxLength={500}
              rows={3}
              value={problemDescription}
              onChange={(event) => setProblemDescription(event.target.value)}
              disabled={isSubmitting}
            />
            <small>{problemDescription.length}/500</small>
          </label>
          <label className="fuel-plan-survey-comment">
            What did you expect instead? <span>Optional</span>
            <textarea
              maxLength={500}
              rows={3}
              value={expectedBehavior}
              onChange={(event) => setExpectedBehavior(event.target.value)}
              disabled={isSubmitting}
            />
            <small>{expectedBehavior.length}/500</small>
          </label>
          {errorMessage && (
            <p className="fuel-plan-survey-error" role="alert">
              {errorMessage}
            </p>
          )}
          <div className="fuel-plan-survey-actions">
            <button
              disabled={isSubmitting || !problemDescription.trim()}
              type="submit"
            >
              {isSubmitting ? "Submitting..." : "Submit feedback"}
            </button>
            <button
              disabled={isSubmitting}
              onClick={() => setIsOpen(false)}
              type="button"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
      {alreadySubmitted && (
        <div className="fuel-plan-survey-confirmation" role="status">
          <p>
            {wasPreviouslySubmitted
              ? snapshot.generationId
                ? "Feedback has already been submitted for this fuel plan."
                : "Feedback has already been submitted for this load."
              : "Thank you for reporting this issue. Your submission has been received."}
          </p>
          {!emailSent && (
            <div>
              <p>Email delivery is pending. Your response is saved.</p>
              <button disabled={isChecking} onClick={retryEmail} type="button">
                {isChecking ? "Retrying..." : "Retry email"}
              </button>
            </div>
          )}
        </div>
      )}
      {!isOpen && errorMessage && (
        <p className="fuel-plan-survey-error" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );

  return (
    <>
      {renderLayout ? (
        renderLayout(action, details)
      ) : (
        <section
          className="fuel-plan-survey"
          aria-label="Report a fuel plan problem"
        >
          {action}
          {details}
        </section>
      )}
    </>
  );
};

export default FuelPlanSurvey;
